/**
 * Docs (slice 2d spec §10.2; Paper boards 47–47d): what the crew has written
 * and what people added. All and Archived (`?tab=archived`), a search
 * (`?q=`) whose rows come best match first with the match highlighted, a
 * table on a desktop and a list on a phone. A row opens the doc page; New
 * doc opens its window; Upload (slice 5b) makes each file picked a file doc.
 */
import { Button } from "@crew/ui/components/button";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@crew/ui/components/input-group";
import { Fab } from "@crew/ui/components/fab";
import { Link } from "@crew/ui/components/link";
import { Spinner } from "@crew/ui/components/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@crew/ui/components/table";
import { Tabs, TabsList, TabsTrigger } from "@crew/ui/components/tabs";
import { PageTitle } from "@crew/ui/components/typography";
import { FileText, Plus, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { shortTime } from "../chats/format";
import { useDocs, type DocCounts, type DocItem, type DocsTab } from "./api";
import { resultsLine, rowSnippet, searchTextFor } from "./doc-view";
import { NewDocWindow } from "./new-doc";
import { DocIcon, Highlighted, LinkedTo, WhoChip } from "./parts";
import { UploadButton, UploadTray, useDocUploads } from "../files/doc-uploads";
import { AreaFilter } from "../areas/area-controls";
import { useAreaFilter } from "../areas/use-area-filter";
import { filingArea } from "../areas/area-view";
import { AreaChip } from "../areas/parts";

const TABS: Array<[DocsTab, string]> = [
  ["all", "All"],
  ["archived", "Archived"],
];

/** How long the search waits for typing to stop before it asks. */
const SEARCH_WAIT_MS = 250;

const tabOf = (v: string | null): DocsTab => (v === "archived" ? "archived" : "all");

function DocTabs({
  tab,
  counts,
  onChange,
}: {
  tab: DocsTab;
  counts: DocCounts | undefined;
  onChange: (t: DocsTab) => void;
}) {
  return (
    <Tabs value={tab} onValueChange={(v) => onChange(v as DocsTab)}>
      <TabsList aria-label="Which docs">
        {TABS.map(([t, label]) => (
          <TabsTrigger key={t} value={t} data-tab={t}>
            {label}
            <span className="text-muted-foreground tabular">{counts?.[t] ?? ""}</span>
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

function SearchBox({ value, onChange }: { value: string; onChange: (q: string) => void }) {
  return (
    <InputGroup className="h-10 w-full rounded-lg bg-card md:max-w-[420px]">
      <InputGroupAddon>
        <Search />
      </InputGroupAddon>
      <InputGroupInput
        type="search"
        aria-label="Search docs"
        placeholder="Search docs"
        value={value}
        maxLength={200}
        onChange={(e) => onChange(e.target.value)}
        className="text-base md:text-[15px]"
      />
    </InputGroup>
  );
}

/** The title (its match marked when only the title matched) and the snippet under it. */
function DocCell({ item, searching }: { item: DocItem; searching: boolean }) {
  const line = rowSnippet(item, searching);
  return (
    <>
      <span className="font-medium text-foreground">
        {item.file ? (
          <DocIcon file={item.file} className="mr-1.5 inline size-3.5 align-[-2px]" />
        ) : null}
        <Highlighted text={item.title} marks={searching ? item.marks.title : []} />
        <AreaChip area={item.area} className="ml-2" />
      </span>
      {line.text ? (
        <span className="line-clamp-1 text-muted-foreground">
          {line.passage ? "…" : null}
          <Highlighted text={line.text} marks={line.marks} />
          {line.passage ? "…" : null}
        </span>
      ) : null}
    </>
  );
}

function DocTable({ items, me, searching }: { items: DocItem[]; me: Me; searching: boolean }) {
  return (
    <div className="hidden md:block" data-docs-table>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Doc</TableHead>
            <TableHead className="w-[25%]">Linked to</TableHead>
            <TableHead className="w-[170px]">Last changed by</TableHead>
            <TableHead className="w-[90px] text-right">Updated</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.id} data-doc-id={item.id} className="relative">
              <TableCell className="max-w-0 whitespace-normal">
                <Link
                  href={`/docs/${item.id}`}
                  className="flex flex-col gap-0.5 after:absolute after:inset-0"
                >
                  <DocCell item={item} searching={searching} />
                </Link>
              </TableCell>
              <TableCell className="max-w-0 text-copy">
                <LinkedTo item={item} />
              </TableCell>
              <TableCell>
                <WhoChip who={item.updated_by} me={me} />
              </TableCell>
              <TableCell className="text-right text-muted-foreground">
                {shortTime(item.updated_at, me.workspace.timezone)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/** The phone's list (47b): title, the match when searching, linked to, updated. */
function DocList({ items, me, searching }: { items: DocItem[]; me: Me; searching: boolean }) {
  return (
    <ul
      className="flex flex-col divide-y divide-border border-b border-border md:hidden"
      data-docs-list
    >
      {items.map((item) => {
        const line = rowSnippet(item, searching);
        return (
          <li key={item.id} data-doc-id={item.id}>
            <Link href={`/docs/${item.id}`} className="flex flex-col gap-1.5 py-3.5">
              <span className="text-body font-medium text-foreground">
                {item.file ? (
                  <DocIcon file={item.file} className="mr-1.5 inline size-3.5 align-[-2px]" />
                ) : null}
                <Highlighted text={item.title} marks={searching ? item.marks.title : []} />
                <AreaChip area={item.area} className="ml-2" />
              </span>
              {line.passage || item.file ? (
                <span className="line-clamp-1 text-meta text-copy">
                  {line.passage ? "…" : null}
                  <Highlighted text={line.text} marks={line.marks} />
                  {line.passage ? "…" : null}
                </span>
              ) : null}
              <span className="flex items-center justify-between gap-3 text-meta text-copy">
                <LinkedTo item={item} className="min-w-0" />
                <span className="shrink-0 text-muted-foreground">
                  {shortTime(item.updated_at, me.workspace.timezone)}
                </span>
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/** No docs at all (47d), or none matching a search, or none archived. */
function Empty({ tab, q }: { tab: DocsTab; q: string }) {
  if (q) {
    return (
      <p
        data-docs-empty="search"
        className="px-6 py-14 text-center text-body text-muted-foreground"
      >
        Nothing matches “{q}”.
      </p>
    );
  }
  if (tab === "archived") {
    return (
      <p
        data-docs-empty="archived"
        className="px-6 py-14 text-center text-body text-muted-foreground"
      >
        No archived docs. An archived doc keeps its text and links, out of the list.
      </p>
    );
  }
  return (
    <div data-docs-empty="all" className="flex flex-col items-center gap-3 px-6 py-16 text-center">
      <FileText aria-hidden strokeWidth={1.5} className="size-9 text-muted-foreground" />
      <h2 className="font-display text-title leading-9 text-foreground">No docs yet</h2>
      <p className="max-w-[420px] text-body text-muted-foreground">
        Ask for a write-up, a plan or a list, start a New doc, or upload a file.
      </p>
    </div>
  );
}

type DocsQuery = ReturnType<typeof useDocs>;

/** Past the first page: a failed page's reason, and Older docs. */
function MorePages({ docs }: { docs: DocsQuery }) {
  return (
    <>
      {docs.isFetchNextPageError ? (
        <p role="alert" className="self-center text-meta text-brick">
          {errorHint(docs.error)}
        </p>
      ) : null}
      {docs.hasNextPage ? (
        <Button
          variant="ghost"
          className="self-center"
          disabled={docs.isFetchingNextPage}
          onClick={() => void docs.fetchNextPage()}
        >
          Older docs
        </Button>
      ) : null}
    </>
  );
}

function DocsRows({
  docs,
  pages,
  tab,
  q,
  me,
}: {
  docs: DocsQuery;
  pages: NonNullable<DocsQuery["data"]>["pages"];
  tab: DocsTab;
  q: string;
  me: Me;
}) {
  const items = pages.flatMap((p) => p.items);
  const searching = q.length > 0;
  return (
    <div className="flex flex-col gap-3">
      {docs.isRefetchError ? (
        <p role="alert" className="text-meta text-brick">
          Couldn't refresh the docs: these may be out of date. {errorHint(docs.error)}
        </p>
      ) : null}
      {searching && items.length > 0 ? (
        <p data-docs-results className="text-meta text-muted-foreground">
          {resultsLine(pages[0]?.counts[tab] ?? items.length)}
        </p>
      ) : null}
      {items.length === 0 ? (
        <Empty tab={tab} q={q} />
      ) : (
        <>
          <DocTable items={items} me={me} searching={searching} />
          <DocList items={items} me={me} searching={searching} />
        </>
      )}
      <MorePages docs={docs} />
    </div>
  );
}

function DocsBody({ tab, q, area, me }: { tab: DocsTab; q: string; area: string | null; me: Me }) {
  const docs = useDocs(tab, q, area);
  if (docs.data) return <DocsRows docs={docs} pages={docs.data.pages} tab={tab} q={q} me={me} />;
  // Only when nothing loaded: a failed Older docs keeps the rows it has.
  if (docs.error) {
    return <p className="text-body text-muted-foreground">{errorHint(docs.error)}</p>;
  }
  return (
    <div className="flex justify-center py-10 text-muted-foreground">
      <Spinner className="size-5" />
    </div>
  );
}

/** The search box's text, written to `?q=` once typing stops. */
function useSearchText(q: string, setQ: (q: string) => void) {
  const [text, setText] = useState(q);
  // The URL's `q` as last seen: Back, Forward or a link can change it under
  // the box, which then shows it, rather than writing its old text back (Devin on #275).
  const [seen, setSeen] = useState(q);
  if (q !== seen) {
    setSeen(q);
    setText(searchTextFor(text, q));
  }
  useEffect(() => {
    if (text.trim() === q) return;
    const wait = setTimeout(() => setQ(text.trim()), SEARCH_WAIT_MS);
    return () => clearTimeout(wait);
  }, [text, q, setQ]);
  return [text, setText] as const;
}

export function DocsPage({ me }: { me: Me }) {
  const [params, setParams] = useSearchParams();
  const tab = tabOf(params.get("tab"));
  const q = (params.get("q") ?? "").trim();
  const [area, setArea] = useAreaFilter();
  const counts = useDocs(tab, q, area).data?.pages[0]?.counts;
  const [creating, setCreating] = useState(false);
  // Slice 5b (spec §6.1): each file uploaded here is a file doc, filed in the Area shown.
  const uploads = useDocUploads({ areaId: filingArea(area, me) });
  const set = (key: string, value: string | null) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  const [text, setText] = useSearchText(q, (next) => set("q", next || null));
  return (
    <main className="flex w-full max-w-6xl flex-col gap-5 px-4 pt-6 pb-16 sm:px-6 md:gap-6 md:px-12 md:pt-9">
      <div className="flex items-end justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1.5">
          <PageTitle>Docs</PageTitle>
          <p className="text-sm text-muted-foreground">
            <span className="md:hidden">What the crew has written.</span>
            <span className="hidden md:inline">
              What the crew has written, and what you've added.
            </span>
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <UploadButton
            variant="outline"
            onFiles={uploads.pick}
            data-upload-button
            className="hidden md:inline-flex"
          />
          <UploadButton
            variant="outline"
            size="icon"
            onFiles={uploads.pick}
            data-upload-icon
            className="md:hidden"
          />
          <Button
            onClick={() => setCreating(true)}
            data-new-doc-button
            className="hidden md:inline-flex"
          >
            <Plus />
            New doc
          </Button>
        </div>
      </div>
      <UploadTray docs={uploads} />
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
        <SearchBox value={text} onChange={setText} />
        <DocTabs tab={tab} counts={counts} onChange={(t) => set("tab", t === "all" ? null : t)} />
        <AreaFilter me={me} value={area} onChange={setArea} className="md:ml-auto" />
      </div>
      <DocsBody tab={tab} q={q} area={area} me={me} />
      <Fab label="New doc" onClick={() => setCreating(true)} data-new-doc-fab>
        <Plus />
      </Fab>
      <NewDocWindow open={creating} onOpenChange={setCreating} areaId={filingArea(area, me)} />
    </main>
  );
}
